// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import {ComponentBus} from '@app/features/platform/utils/ComponentBus';
import Users from '@app/features/user/state/Users';
import {
	DEFAULT_SOUNDBOARD_SOUNDS,
	type SoundboardSound,
	synthesizeSoundEffect,
} from '@app/features/voice/soundboard/SoundboardPresets';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {makeAutoObservable} from 'mobx';

export interface SoundboardActiveToast {
	sound: SoundboardSound;
	userId: string;
	username: string;
	timestamp: number;
}

export const MAX_PERSONAL_SOUNDBOARD_SLOTS = 10;

/**
 * Server soundboard slot limit based on Discord-style Guild Boost Tier:
 * Tier 0 (No Boost): 8 slots
 * Tier 1: 24 slots
 * Tier 2: 36 slots
 * Tier 3: 48 slots
 */
export function getServerSoundLimit(premiumTier = 0): number {
	switch (premiumTier) {
		case 1:
			return 24;
		case 2:
			return 36;
		case 3:
			return 48;
		default:
			return 8;
	}
}

const STORAGE_KEY_GUILD_SOUNDS = 'fluxer:soundboard:guild_sounds';
const STORAGE_KEY_PERSONAL_SOUNDS = 'fluxer:soundboard:personal_sounds';
const STORAGE_KEY_LEGACY_CUSTOM = 'fluxer:soundboard:custom_sounds';
const STORAGE_KEY_VOLUME = 'fluxer:soundboard:volume';

class SoundboardStore {
	guildSounds: Record<string, Array<SoundboardSound>> = {};
	personalSounds: Array<SoundboardSound> = [];
	volume = 75; // 0 - 100
	activeToast: SoundboardActiveToast | null = null;
	private toastTimer: NodeJS.Timeout | null = null;
	private audioContext: AudioContext | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.loadSavedState();

		if (typeof window !== 'undefined') {
			ComponentBus.subscribe('VOICE_SOUNDBOARD_PLAY', (data?: unknown) => {
				const payload = data as {
					soundId?: string;
					userId?: string;
					username?: string;
					customUrl?: string;
					soundName?: string;
					soundEmoji?: string;
				};
				if (!payload?.soundId) return;
				this.onRemoteSoundPlayed(
					payload.soundId,
					payload.userId,
					payload.username,
					payload.customUrl,
					payload.soundName,
					payload.soundEmoji,
				);
			});
		}
	}

	private loadSavedState(): void {
		if (typeof window === 'undefined') return;
		try {
			const savedVol = localStorage.getItem(STORAGE_KEY_VOLUME);
			if (savedVol !== null) {
				const parsed = Number(savedVol);
				if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 100) {
					this.volume = parsed;
				}
			}

			// Load Server Sounds
			const savedGuild = localStorage.getItem(STORAGE_KEY_GUILD_SOUNDS);
			if (savedGuild) {
				const map = JSON.parse(savedGuild);
				if (typeof map === 'object' && map !== null) {
					this.guildSounds = map;
				}
			}

			// Load Personal Sounds
			const savedPersonal = localStorage.getItem(STORAGE_KEY_PERSONAL_SOUNDS);
			if (savedPersonal) {
				const list = JSON.parse(savedPersonal);
				if (Array.isArray(list)) {
					this.personalSounds = list;
				}
			}

			// Legacy migration: migrate any old customSounds to personalSounds
			const savedLegacy = localStorage.getItem(STORAGE_KEY_LEGACY_CUSTOM);
			if (savedLegacy && this.personalSounds.length === 0) {
				const list = JSON.parse(savedLegacy);
				if (Array.isArray(list) && list.length > 0) {
					this.personalSounds = list.slice(0, MAX_PERSONAL_SOUNDBOARD_SLOTS).map((s) => ({
						...s,
						category: 'personal',
					}));
					this.savePersonalSounds();
				}
			}
		} catch {
			// LocalStorage unavailable
		}
	}

	private saveGuildSounds(): void {
		if (typeof window === 'undefined') return;
		try {
			localStorage.setItem(STORAGE_KEY_GUILD_SOUNDS, JSON.stringify(this.guildSounds));
		} catch {}
	}

	private savePersonalSounds(): void {
		if (typeof window === 'undefined') return;
		try {
			localStorage.setItem(STORAGE_KEY_PERSONAL_SOUNDS, JSON.stringify(this.personalSounds));
		} catch {}
	}

	setVolume(vol: number): void {
		this.volume = Math.max(0, Math.min(100, vol));
		try {
			localStorage.setItem(STORAGE_KEY_VOLUME, String(this.volume));
		} catch {}
	}

	getGuildSounds(guildId: string): Array<SoundboardSound> {
		return this.guildSounds[guildId] ?? [];
	}

	getPersonalSounds(): Array<SoundboardSound> {
		return this.personalSounds;
	}

	getAllSoundsForContext(guildId?: string): ReadonlyArray<SoundboardSound> {
		const serverSounds = guildId ? this.getGuildSounds(guildId) : [];
		return [...DEFAULT_SOUNDBOARD_SOUNDS, ...serverSounds, ...this.personalSounds];
	}

	get allSounds(): ReadonlyArray<SoundboardSound> {
		const allServerSounds = Object.values(this.guildSounds).flat();
		return [...DEFAULT_SOUNDBOARD_SOUNDS, ...allServerSounds, ...this.personalSounds];
	}

	findSound(soundId: string): SoundboardSound | undefined {
		const preset = DEFAULT_SOUNDBOARD_SOUNDS.find((s) => s.id === soundId);
		if (preset) return preset;

		const personal = this.personalSounds.find((s) => s.id === soundId);
		if (personal) return personal;

		for (const list of Object.values(this.guildSounds)) {
			const found = list.find((s) => s.id === soundId);
			if (found) return found;
		}

		return undefined;
	}

	addGuildSound(
		guildId: string,
		name: string,
		emoji: string,
		url: string,
		premiumTier = 0,
	): {success: boolean; error?: string} {
		const limit = getServerSoundLimit(premiumTier);
		const current = this.guildSounds[guildId] ?? [];
		if (current.length >= limit) {
			return {
				success: false,
				error: `Server soundboard slots full (${limit} slots). Boost server to Level ${Math.min(3, premiumTier + 1)} to unlock more slots!`,
			};
		}

		const newSound: SoundboardSound = {
			id: `guild_${guildId}_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
			name: name.trim() || 'Server Sound',
			emoji: emoji.trim() || '🔊',
			category: 'server',
			guildId,
			customUrl: url.trim(),
		};

		if (!this.guildSounds[guildId]) {
			this.guildSounds[guildId] = [];
		}
		this.guildSounds[guildId].push(newSound);
		this.saveGuildSounds();
		return {success: true};
	}

	removeGuildSound(guildId: string, soundId: string): void {
		if (!this.guildSounds[guildId]) return;
		this.guildSounds[guildId] = this.guildSounds[guildId].filter((s) => s.id !== soundId);
		this.saveGuildSounds();
	}

	addPersonalSound(
		name: string,
		emoji: string,
		url: string,
		isPremium: boolean,
	): {success: boolean; error?: string} {
		if (!isPremium) {
			return {
				success: false,
				error: 'ND+ membership is required to save personal soundboard sounds.',
			};
		}

		if (this.personalSounds.length >= MAX_PERSONAL_SOUNDBOARD_SLOTS) {
			return {
				success: false,
				error: `Personal soundboard slots full (${MAX_PERSONAL_SOUNDBOARD_SLOTS}/10). Delete a sound to add a new one.`,
			};
		}

		const newSound: SoundboardSound = {
			id: `personal_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
			name: name.trim() || 'Personal Sound',
			emoji: emoji.trim() || '🔊',
			category: 'personal',
			customUrl: url.trim(),
		};

		this.personalSounds.push(newSound);
		this.savePersonalSounds();
		return {success: true};
	}

	removePersonalSound(soundId: string): void {
		this.personalSounds = this.personalSounds.filter((s) => s.id !== soundId);
		this.savePersonalSounds();
	}

	private getAudioContext(): AudioContext | null {
		if (typeof window === 'undefined') return null;
		if (!this.audioContext || this.audioContext.state === 'closed') {
			const Ctor =
				window.AudioContext ||
				(window as unknown as {webkitAudioContext: typeof AudioContext}).webkitAudioContext;
			if (!Ctor) return null;
			this.audioContext = new Ctor();
		}
		if (this.audioContext.state === 'suspended') {
			void this.audioContext.resume();
		}
		return this.audioContext;
	}

	previewSound(sound: SoundboardSound): void {
		this.triggerAudioPlayback(sound);
	}

	async playSound(soundId: string): Promise<void> {
		const sound = this.findSound(soundId);
		if (!sound) return;

		const currentUserId = Authentication.currentUserId || 'unknown';
		const currentUser = Users.getUser(currentUserId);
		const username = currentUser?.username || 'Someone';

		// Trigger local audio playback
		this.triggerAudioPlayback(sound);

		// Display toast locally
		this.showSoundToast(sound, currentUserId, username);

		// Broadcast to all participants in the voice channel through LiveKit data channel
		const room = MediaEngine.room;
		if (room?.localParticipant && room.state === 'connected') {
			try {
				const message = JSON.stringify({
					type: 'SOUNDBOARD_PLAY',
					soundId: sound.id,
					soundName: sound.name,
					soundEmoji: sound.emoji,
					customUrl: sound.customUrl,
					userId: currentUserId,
					username,
				});
				const encoded = new TextEncoder().encode(message);
				void room.localParticipant.publishData(encoded, {
					reliable: true,
					topic: 'soundboard',
				});
			} catch (err) {
				console.warn('Failed to publish soundboard packet', err);
			}
		}

		// Also broadcast locally across components (e.g. for speaking ring)
		ComponentBus.dispatch('VOICE_SOUNDBOARD_PLAY', {
			soundId: sound.id,
			soundName: sound.name,
			soundEmoji: sound.emoji,
			customUrl: sound.customUrl,
			userId: currentUserId,
			username,
			channelId: MediaEngine.channelId,
		});
	}

	onRemoteSoundPlayed(
		soundId: string,
		userId?: string,
		username?: string,
		customUrl?: string,
		soundName?: string,
		soundEmoji?: string,
	): void {
		// Ignore self-broadcast looped back
		if (userId && userId === Authentication.currentUserId) return;

		let sound = this.findSound(soundId);
		if (!sound && customUrl) {
			sound = {
				id: soundId,
				name: soundName || 'Custom Sound',
				emoji: soundEmoji || '🔊',
				category: 'server',
				customUrl,
			};
		}
		if (!sound) return;

		// Play sound through recipient's output
		this.triggerAudioPlayback(sound);

		// Show notification toast in VC
		this.showSoundToast(sound, userId || 'unknown', username || 'Someone');
	}

	private showSoundToast(sound: SoundboardSound, userId: string, username: string): void {
		this.activeToast = {
			sound,
			userId,
			username,
			timestamp: Date.now(),
		};

		if (this.toastTimer) {
			clearTimeout(this.toastTimer);
		}
		this.toastTimer = setTimeout(() => {
			this.activeToast = null;
			this.toastTimer = null;
		}, 3000);
	}

	private triggerAudioPlayback(sound: SoundboardSound): void {
		const ctx = this.getAudioContext();
		if (!ctx) return;

		const masterGain = ctx.createGain();
		const volumeFactor = (this.volume / 100) * (VoiceSettings.getOutputVolume() / 100);
		masterGain.gain.setValueAtTime(Math.max(0, Math.min(1.5, volumeFactor)), ctx.currentTime);
		masterGain.connect(ctx.destination);

		if (sound.category === 'built-in') {
			synthesizeSoundEffect(ctx, sound.id, masterGain);
		} else if (sound.customUrl) {
			const audio = new Audio(sound.customUrl);
			audio.volume = Math.max(0, Math.min(1, volumeFactor));
			void audio.play().catch(() => {});
		}
	}
}

export default new SoundboardStore();
